import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Book, Plus, X } from "lucide-react";

const TYPE_STYLES = {
  asset:     "bg-blue-100 text-blue-700",
  liability: "bg-purple-100 text-purple-700",
  equity:    "bg-slate-100 text-slate-700",
  revenue:   "bg-emerald-100 text-emerald-700",
  expense:   "bg-amber-100 text-amber-700",
};

export default function AdminChartOfAccounts() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { const r = await api.get("/accounts?active_only=false"); setRows(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-chart-of-accounts" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Finance</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
            Chart of Accounts
          </h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            The double-entry ledger's account list. Codes follow the 1xxx (assets) / 2xxx (liab) / 3xxx (equity) / 4xxx (rev) / 5-6xxx (exp) convention.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="new-account-btn">
          <Plus className="w-4 h-4" /> New account
        </button>
      </div>

      <div className="card-pb overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] dark:bg-slate-800 text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
            <tr>
              <th className="text-left px-4 py-2 w-24">Code</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2 w-24">Type</th>
              <th className="text-left px-4 py-2 w-24">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.account_id} data-testid={`account-row-${r.code}`}
                  className="border-t border-[#E2E8F0] dark:border-slate-700 hover:bg-[#F8FAFC] dark:hover:bg-slate-800">
                <td className="px-4 py-2 font-mono text-[#0A2540] dark:text-white">{r.code}</td>
                <td className="px-4 py-2">
                  <span className="text-[#0A2540] dark:text-white inline-flex items-center gap-1.5">
                    <Book className="w-3 h-3 text-[#C9A356]" /> {r.name}
                  </span>
                  {r.is_system && <span className="text-[9px] uppercase tracking-wider text-[#94A3B8] ml-2">system</span>}
                </td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${TYPE_STYLES[r.type]}`}>
                    {r.type}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] uppercase tracking-wider ${r.is_active ? "text-emerald-600" : "text-red-500"}`}>
                    {r.is_active ? "active" : "inactive"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <AccountModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function AccountModal({ onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("expense");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!code.trim() || !name.trim()) { toast.error("Code and name required"); return; }
    setSaving(true);
    try {
      await api.post("/accounts", { code, name, type });
      toast.success("Account created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">New account</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Code *</label>
            <input className={cls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="6200" data-testid="account-code" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Name *</label>
            <input className={cls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Marketing Expense" data-testid="account-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Type *</label>
            <select className={cls} value={type} onChange={(e) => setType(e.target.value)} data-testid="account-type">
              <option value="asset">Asset</option>
              <option value="liability">Liability</option>
              <option value="equity">Equity</option>
              <option value="revenue">Revenue</option>
              <option value="expense">Expense</option>
            </select>
          </div>
          <button onClick={save} disabled={saving} className="btn-accent text-sm w-full disabled:opacity-50" data-testid="account-save">
            {saving ? "Saving…" : "Create account"}
          </button>
        </div>
      </div>
    </div>
  );
}
